import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot} from '@angular/router';
import {gql} from 'apollo-angular';
import {AppModel} from 'app/features/apps/classes/app-model';
import {
    AppAttributionSettings,
    AppCreator,
    AppOwnerExtended,
    CreateAppAttributes,
    EditAppAttributes,
} from 'app/features/apps/interfaces/app.interface';
import {
    usersMmpAccountsList,
    usersMmpAccountsList_mmpAccounts_nodes,
    usersMmpAccountsListVariables,
} from 'app/features/apps/services/gql/usersMmpAccountsList';
import {ApiService} from 'app/shared/modules/api/services/api.service';
import {map, pluck} from 'rxjs/operators';
import {app as appI, app_app} from 'app/features/apps/services/gql/app';
import {userWithAppOwners} from 'app/features/apps/services/gql/userWithAppOwners';
import {createApp} from './gql/createApp';
import {updateApp} from './gql/updateApp';
import {updateAppAttributionSettings} from './gql/updateAppAttributionSettings';
import {refreshStoreInfo} from './gql/refreshStoreInfo';
import {SelectedAppService} from 'app/core/services/current-app/selected-app.service';
import {BehaviorSubject} from 'rxjs';

export const userWithAppOwnersQuery = gql`
    query userWithAppOwners {
        currentUser {
            id
            email
            permissions {
                canManageNetworksAutoconnection
            }
            appsOwners {
                nodes {
                    id
                    email
                    name
                    networkAccounts {
                        admob {
                            id
                            login
                        }
                    }
                    permissions {
                        canCreateApp
                        canViewNetworkAccount
                    }
                }
            }
        }
    }
`;
export const appQuery = gql`
    query app($id: ID!) {
        app(id: $id) {
            id
            platform
            name
            bundleId
            iconUrl
            applicationKey
            storeUrl
            filterMatureContent
            coppa
            enableStackCmp
            interstitialMode
            disableAdmobSync
            s2sRewardCallbackUrl
            s2sRewardCallbackKey
            googlePlayConsoleCredentials
            testMode
            testDeviceIds
            ownerId
            mediationEngine
            revenueShares {
                email
                percent
            }
            permissions {
                canUpdateBundleId
                canViewAdNetworkMonitor
                canViewLineItems
                canViewConfiguration
                canUseAdmobEmergencyAlgorithm
                canViewMergedInterstitials
                canViewNewMediationSettings
            }
            attributionSettings {
                enableStackAttribution
                primaryMmpAccount
                secondaryMmpAccount
                getSpendFromSecondaryMmpAccount
                primaryMmpRawDataSource
                secondaryMmpRawDataSource
                attributionPlatform
                adjustAppToken
                adjustS2sToken
                adjustEnvironment
                appsflyerDevKey
                appsflyerAppId
                firebaseConfigKeys
                firebaseExpirationDuration
                firebaseTracking
                firebaseAdRevenueKey
                facebookTracking
                sendAfAdRevenue
                sendAfAdRevenueAttribution
                unityS2sIntegration
                iapRevenueAppStoreFeePercent
                iapCustomEvents
            }
            unifiedApp {
                id
                androidId
                iosId
                name
                ownerId
            }
        }
    }
`;
const createAppMutation = gql`
    mutation createApp($attrs: CreateAppAttributes!) {
        createApp(attributes: $attrs) {
            applicationKey
        }
    }
`;
const updateAppMutation = gql`
    mutation updateApp($appId: ID!, $attrs: UpdateAppAttributes!) {
        updateApp(appId: $appId, attributes: $attrs) {
            applicationKey
        }
    }
`;
const updateAppAttributionSettingsMutation = gql`
    mutation updateAppAttributionSettings($appId: ID!, $attrs: AppAttributionSettingsAttributes!) {
        updateAppAttributionSettings(appId: $appId, attributes: $attrs) {
            enableStackAttribution
            primaryMmpAccount
            secondaryMmpAccount
            getSpendFromSecondaryMmpAccount
            attributionPlatform
            adjustAppToken
            adjustS2sToken
            adjustEnvironment
            appsflyerDevKey
            appsflyerAppId
            firebaseConfigKeys
            firebaseExpirationDuration
            firebaseTracking
            firebaseAdRevenueKey
            facebookTracking
            sendAfAdRevenue
            sendAfAdRevenueAttribution
            unityS2sIntegration
            iapRevenueAppStoreFeePercent
            iapCustomEvents
        }
    }
`;
const refreshStoreInfoMutation = gql`
    mutation refreshStoreInfo($appId: ID!) {
        refreshStoreInfo(appId: $appId) {
            status
            storeUrl
        }
    }
`;

const usersMmpAccountsListQuery = gql`
    query usersMmpAccountsList($userId: ID) {
        mmpAccounts(userId: $userId) {
            nodes {
                id
                name
                ... on AdjustAccount {
                    useS3
                }
                __typename
            }
        }
    }
`;

export interface AppDetailedInfo {
    app: AppModel;
    creator: AppCreator;
    appOwners: Map<string, AppOwnerExtended>;
    mmpAccounts: usersMmpAccountsList_mmpAccounts_nodes[];
}

@Injectable()
export class ManageAppService {
    appModel$: BehaviorSubject<app_app> = new BehaviorSubject<app_app>(null);

    constructor(
        private apiService: ApiService,
        private selectedAppService: SelectedAppService
    ) {}

    async resolve(route: ActivatedRouteSnapshot): Promise<AppDetailedInfo> {
        let creator = await this.loadUser();
        let appOwners = creator.appsOwners.nodes;

        let app: AppModel;
        let mmpAccounts = [];
        const appId = route.params.appId || route.queryParams['appId'];
        if (appId) {
            let getApp = await this.fetchApp(appId);
            this.appModel$.next(getApp);
            this.selectedAppService.currentAppModel.next(getApp);
            app = new AppModel(getApp as unknown as AppModel);
        } else {
            app = new AppModel(AppModel.getDefaultModel(route.queryParams.appOwnerId || creator.id));
        }

        if (route.pathFromRoot) {
            await this.selectedAppService.extractFromRouter(route);
        }

        if (route.data.mmpAccountsRequired) {
            mmpAccounts = await this.loadUserMmpAccounts(app.ownerId);
        }

        return {
            app,
            creator,
            mmpAccounts,
            appOwners: new Map(
                appOwners.filter(owner => owner.permissions.canCreateApp).map<[string, AppOwnerExtended]>(owner => [owner.id, owner])
            ),
        };
    }

    private loadUserMmpAccounts(userId: string) {
        return this.apiService
            .query<usersMmpAccountsList, usersMmpAccountsListVariables>({
                query: usersMmpAccountsListQuery,
                variables: {userId},
            })
            .pipe(pluck('mmpAccounts', 'nodes'))
            .toPromise();
    }

    private loadUser() {
        return this.apiService
            .query<userWithAppOwners>({
                query: userWithAppOwnersQuery,
            })
            .pipe(map(r => r.currentUser))
            .toPromise();
    }

    createApp(createAppDto: CreateAppAttributes) {
        return this.apiService
            .mutate<createApp>({
                mutation: createAppMutation,
                variables: {
                    attrs: createAppDto,
                },
            })
            .pipe(map(r => r.createApp));
    }

    updateApp(appId: string, updateAppDto: EditAppAttributes) {
        return this.apiService
            .mutate<updateApp>({
                mutation: updateAppMutation,
                variables: {
                    appId,
                    attrs: updateAppDto,
                },
            })
            .pipe(map(r => r.updateApp));
    }

    updateAppAttributionSettings(appId: string, updateAppAttributionSettingsDto: AppAttributionSettings) {
        return this.apiService
            .mutate<updateAppAttributionSettings>({
                mutation: updateAppAttributionSettingsMutation,
                variables: {
                    appId,
                    attrs: updateAppAttributionSettingsDto,
                },
            })
            .pipe(map(r => r.updateAppAttributionSettings));
    }

    fetchApp(appId: string) {
        return this.apiService
            .query<appI>({
                query: appQuery,
                variables: {
                    id: appId,
                },
                fetchPolicy: 'network-only',
            })
            .pipe(map(r => r.app))
            .toPromise() as Promise<app_app>;
    }

    refreshStoreInfo(appId: string) {
        return this.apiService
            .mutate<refreshStoreInfo>({
                mutation: refreshStoreInfoMutation,
                variables: {
                    appId,
                },
            })
            .pipe(map(r => r.refreshStoreInfo));
    }
}
